import { Text } from './Text';
import { Token, token } from './token';
import { OneOrMany, StringTransform, TextExtension } from './types';
import { compose } from './utils';

export { Text } from './Text';
export type * from './types';

/** Token type - more restrictive than `string` */
export type $<T extends string> = Token<T>;

/** String type - equivalent to `string` */
export type $$ = string;

/** Text type - more expressive than `string` */
export type $$$ = Text;

/**
 * Creates a type-safe string token from a known literal or validated input.
 *
 * Tokens are strings whose values are either statically known at compile time
 * or dynamically validated against an enumerable set or constructable pattern.
 * Returns `''` on validation failure, enabling falsy checks for narrowing.
 *
 * @example Static token (compile-time literal)
 * ```ts
 * const status = $('pending');  // Token<'pending'>
 * const obj = { type: $('request') };  // discriminated union tag
 * ```
 *
 * @example Dynamic token with enumerable set
 * ```ts
 * const role = $(userInput, ['admin', 'user', 'guest'] as const);
 * if (role) {
 *   // role is Token<'admin' | 'user' | 'guest'>
 * }
 * ```
 *
 * @example Dynamic token with constructable pattern (template literal)
 * ```ts
 * const userId = $(input, (s): s is `user:${string}` => s.startsWith('user:'));
 * if (userId) {
 *   // userId is Token<`user:${string}`>
 * }
 * ```
 */
export { token as $ };

/**
 * Converts a value to a string, optionally applying transforms (composed in order).
 * `null` and `undefined` become empty strings.
 *
 * @param value the value to convert to a string
 * @param transforms optional transform(s) to apply
 * @return $$ typed value
 */
export function $$(
  value: string | number | boolean | null | undefined,
  transforms?: OneOrMany<StringTransform>,
): $$ {
  const str = Text.from(value).$$;
  if (!transforms) return str;
  if (typeof transforms === 'function') return transforms(str);
  return compose(...transforms)(str);
}

/**
 * Wraps a value as Text, optionally applying trait/mixin extensions.
 * Extensions are applied independently to the base Text instance.
 *
 * @example
 * // Single extension
 * const t = $$$('hello', withValidity(/^h/));
 * if (t.valid) {
 *  console.log(`${t} world!`);
 * }
 *
 * @example
 * // Multiple extensions - returns intersection type
 * const t = $$$('https://firstinmath.com', [asURL(), withValidity(/firstinmath/)]);
 * // t is $$$ & URLExtension & ValidityExtension
 * if (t.valid) {
 *   console.log(t.parsed.hostname);
 * }
 *
 * @return Extended Text instance with all extension properties merged
 */
export function $$$(
  value: Text | string | number | boolean | null | undefined,
): $$$;
export function $$$<T1 extends Text>(
  value: Text | string | number | boolean | null | undefined,
  extension: TextExtension<T1>,
): $$$ & T1;
export function $$$<T1 extends Text, T2 extends Text>(
  value: Text | string | number | boolean | null | undefined,
  extensions: [TextExtension<T1>, TextExtension<T2>],
): $$$ & T1 & T2;
export function $$$<T1 extends Text, T2 extends Text, T3 extends Text>(
  value: Text | string | number | boolean | null | undefined,
  extensions: [TextExtension<T1>, TextExtension<T2>, TextExtension<T3>],
): $$$ & T1 & T2 & T3;
export function $$$<
  T1 extends Text,
  T2 extends Text,
  T3 extends Text,
  T4 extends Text,
>(
  value: Text | string | number | boolean | null | undefined,
  extensions: [
    TextExtension<T1>,
    TextExtension<T2>,
    TextExtension<T3>,
    TextExtension<T4>,
  ],
): $$$ & T1 & T2 & T3 & T4;
export function $$$<
  T1 extends Text,
  T2 extends Text,
  T3 extends Text,
  T4 extends Text,
  T5 extends Text,
>(
  value: Text | string | number | boolean | null | undefined,
  extensions: [
    TextExtension<T1>,
    TextExtension<T2>,
    TextExtension<T3>,
    TextExtension<T4>,
    TextExtension<T5>,
  ],
): $$$ & T1 & T2 & T3 & T4 & T5;
export function $$$(
  value: Text | string | number | boolean | null | undefined,
  extensions?: OneOrMany<TextExtension>,
): $$$ {
  let text: $$$ = Text.from(value);
  if (extensions === undefined) return text;

  // Normalize to array and chain extend calls
  const extArray =
    typeof extensions === 'function' ? [extensions] : [...extensions];

  for (const ext of extArray) {
    text = text.extend(ext);
  }
  return text;
}
